import { Injectable, inject } from '@angular/core';
import { BiState } from '@services/core/bi.state';
import { PhotoUrlService } from '@services/core/photo-url.service';

/** Pixel transparent : remplace une image introuvable pour masquer l'icône « cassée ». */
const PIXEL_TRANSPARENT = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

/**
 * États de chargement des photos, pour TOUTES les vues (voir styles.scss, « Photos ») :
 *  - pas d'attribut      → en cours de chargement : vignette grise animée ;
 *  - `data-chargee`      → image affichée ;
 *  - `data-image-erreur` → photo introuvable : visuel « photo indisponible ».
 *
 * Écoute en phase de CAPTURE au niveau du document (load/error ne remontent pas) : c'est
 * le seul moyen de couvrir les images injectées en HTML — rendus de phrases en
 * [innerHTML], contenu de l'éditeur manuel CKEditor — sur lesquelles Angular ne peut pas
 * poser de (load)/(error). Jusqu'ici ces images s'affichaient simplement « cassées ».
 */
@Injectable({ providedIn: 'root' })
export class PhotoAffichageService {
	private biState = inject(BiState);
	private photoUrlService = inject(PhotoUrlService);
	private demarre = false;

	init(): void {
		if (this.demarre || typeof document === 'undefined') return;
		this.demarre = true;
		document.addEventListener('load', this.surChargement, true);
		document.addEventListener('error', this.surErreur, true);
	}

	private estPhoto(el: EventTarget | null): el is HTMLImageElement {
		return el instanceof HTMLImageElement
			&& (el.hasAttribute('data-imageid') || el.classList.contains('image-ckeditor'));
	}

	private surChargement = (event: Event): void => {
		const img = event.target;
		if (!this.estPhoto(img)) return;
		// Le pixel transparent « charge » aussi : ce n'est pas la photo, on laisse l'état.
		if (img.currentSrc.startsWith('data:image/gif')) return;
		img.setAttribute('data-chargee', '');
		img.removeAttribute('data-image-erreur');
	};

	private surErreur = (event: Event): void => {
		const img = event.target;
		if (!this.estPhoto(img)) return;
		// Les vignettes de la galerie ont leur propre réparation, liée par Angular
		// (onGalleryImageError) : on n'y touche pas, sinon on écraserait son [src].
		if (!img.classList.contains('image-ckeditor')) return;
		void this.reparer(img);
	};

	private async reparer(img: HTMLImageElement): Promise<void> {
		const photoId = img.getAttribute('data-imageid');
		const src = img.getAttribute('src') || '';

		// Miniature introuvable sur S3 : on bascule sur la photo entière.
		if (photoId && this.photoUrlService.estUrlDirecte(src)) {
			const photo = this.biState.get()?.rapport?.photos?.find(p => p.id === photoId);
			const complete = photo ? this.photoUrlService.repliSurComplete(photo, src) : undefined;
			if (complete) {
				img.setAttribute('src', complete);
				return;
			}
		}

		// URL S3 expirée (onglet ouvert depuis plus de 36 h) : une seule nouvelle tentative.
		if (photoId && this.photoUrlService.estUrlDirecte(src) && !img.hasAttribute('data-reparation')) {
			img.setAttribute('data-reparation', '');
			const biId = this.biState.get()?.id;
			if (biId) {
				const photo = this.biState.get()?.rapport?.photos?.find(p => p.id === photoId);
				const nouvelle = photo ? await this.photoUrlService.rafraichir(biId, photo) : undefined;
				if (nouvelle && nouvelle !== src) {
					img.setAttribute('src', nouvelle);
					return;
				}
			}
		}

		img.setAttribute('data-image-erreur', '');
		img.removeAttribute('data-chargee');
		if (img.getAttribute('src') !== PIXEL_TRANSPARENT) {
			img.setAttribute('src', PIXEL_TRANSPARENT);
		}
	}
}
